import { insertionPlace, type Insertion } from '../../lib/insertion'
import { sendeOrt, useSendKey } from '../../hooks/useSendKey'
import { versatzDesGeraets } from '../../lib/ortszeit'
import { useState } from 'react'
import { useTexte } from '../../texte'
import { createPortal } from 'react-dom'

import { ApiError, netzStand, fehlertext } from '../../api/client'
import { merken } from '../../lib/outbox'
import { usePollAktion } from '../../hooks/queries'
import type { Poll } from '../../api/types'
import { XIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'
import { Blattkopf } from '../../components/Blattkopf'

/** Dieselben Zahlen wie im Server (`PollsController`). */
const MIN_ANTWORTEN = 2
const MAX_ANTWORTEN = 8

/**
 * Eine Abstimmung anlegen (#56).
 *
 * **Zwei leere Felder stehen von Anfang an da.** Eine Abstimmung braucht
 * mindestens zwei Antworten; erst „Antwort hinzufügen" tippen zu müssen, um
 * überhaupt eine zu bekommen, wäre ein Griff für nichts. Weitere kommen mit
 * einem Knopf dazu, und leere fallen beim Senden weg — wer drei ausfüllt und
 * das vierte stehen lässt, hat nichts falsch gemacht.
 *
 * **Höchstens acht, und der Grund steht in der Bedienung**, nicht im
 * Speicher: eine Liste, die zum Rollfeld wird, ist keine Frage mehr, sondern
 * ein Formular. Dieselbe Grenze hält der Server.
 *
 * **Am Seitenkörper** (`createPortal`), wie jedes Blatt hier — sonst klebt es
 * am Verlauf und scrollt mit.
 */
export function Abstimmungsformular({
  tripId,
  onZu,
  entwurf = false,
  vorbelegt,
  insertion = null,
}: {
  tripId: number
  onZu: () => void
  entwurf?: boolean
  /** Before or after a chosen post (#130) -- only with a net, no basket. */
  insertion?: (Insertion & { onDrop: () => void; onDone: (messageId: number) => void }) | null
  /**
   * A poll that is to be changed (1.86.0, from use: "wie bei der Neuanlage,
   * aber keine zusätzlichen Optionen"): the question and the words of the
   * answers that are there -- no answer added, none removed, the choice
   * single/multiple as it is. Votes hang on the answers.
   */
  vorbelegt?: Poll
}) {
  const aktion = usePollAktion(tripId, entwurf)
  const sendeSchluessel = useSendKey(sendeOrt(tripId, undefined, entwurf))
  const [frage, setFrage] = useState(vorbelegt?.question ?? '')
  const [antworten, setAntworten] = useState<string[]>(vorbelegt?.options.map((o) => o.label) ?? ['', ''])
  const [multi, setMulti] = useState(vorbelegt?.multi ?? false)
  const aenderung = vorbelegt !== undefined
  const [problem, setProblem] = useState<string | null>(null)

  const t = useTexte()
  useEscape(onZu)

  const gefuellt = antworten.map((a) => a.trim()).filter((a) => a !== '')
  // **Der Knopf sagt vorher, ob er kann.** Eine Absage nach dem Tippen wäre
  // die stille Absage mit Umweg; hier steht der Grund daneben, solange er gilt.
  // When changing, every answer keeps a word: an emptied one would be a
  // removed one, and that the server refuses.
  const bereit = frage.trim() !== '' && new Set(gefuellt).size >= MIN_ANTWORTEN
    && (!aenderung || gefuellt.length === antworten.length)

  function setzeAntwort(i: number, wert: string): void {
    setAntworten((alt) => alt.map((a, j) => (j === i ? wert : a)))
  }

  async function sende(): Promise<void> {
    setProblem(null)
    if (vorbelegt !== undefined) {
      // Every answer by id with its word -- the server refuses one missing
      // or one unknown. Only online: a change to something that stands is
      // nothing the outbox could hold.
      if (!netzStand()) {
        setProblem(t.allgemein.ohneNetz)
        return
      }
      try {
        await aktion.mutateAsync({
          art: 'aendern',
          pollId: vorbelegt.id,
          question: frage.trim(),
          options: vorbelegt.options.map((o, i) => ({ id: o.id, label: (antworten[i] ?? '').trim() })),
        })
        onZu()
      } catch (fehler) {
        setProblem(fehler instanceof Error ? fehler.message : t.allgemein.schiefgelaufen)
      }
      return
    }
    // The same question and answers keep their key while the sheet is open
    // (1.75.0): a second tap after a lost answer gets the poll that stands.
    // The same key goes into the basket (1.80.0) -- whichever arrives first,
    // it is one poll.
    const clientKey = sendeSchluessel.keyFor(JSON.stringify([frage.trim(), antworten, multi, entwurf, insertion === null ? null : [insertion.where, insertion.id]]))
    /*
     * **Without a net into the basket** (1.80.0, from use: "Abstimmungen auch
     * offline"). Until here the sheet stayed open with "Ohne Netz geht das
     * gerade nicht". A poll is question and answers -- nothing the server
     * has to look up first -- so it waits like a message, and the waiting
     * card stands in the chat (`Abstimmungwartet`).
     */
    const inDenKorb = async () => {
      await merken({
        art: 'abstimmung',
        tripId,
        entwurf,
        bilder: [],
        clientKey,
        abstimmung: { question: frage.trim(), options: antworten, multi, draft: entwurf, tz_offset_min: versatzDesGeraets() },
      })
      sendeSchluessel.forget()
      onZu()
    }
    // Inserting needs the net (#130): the sheet stays, the line says why.
    if (!netzStand() && insertion !== null) {
      setProblem(t.chat.einfuegen.brauchtNetz)
      return
    }
    if (!netzStand()) {
      await inDenKorb()
      return
    }
    try {
      const antwort = await aktion.mutateAsync({
        art: 'anlegen',
        question: frage.trim(),
        options: antworten,
        multi,
        // Into the draft (#85) when opened from the draft page.
        draft: entwurf,
        clientKey,
        insertion: insertionPlace(insertion),
      })
      onZu()
      if (insertion !== null) {
        insertion.onDrop()
        const id = 'message_id' in antwort ? Number((antwort as { message_id: number }).message_id) : 0
        if (id > 0) insertion.onDone(id)
      }
    } catch (fehler) {
      if (fehler instanceof ApiError && fehler.offline && insertion === null) {
        await inDenKorb()
        return
      }
      setProblem(
        fehlertext(fehler),
      )
    }
  }

  return createPortal(
    <div
      className="bogen bogen--abstimmung"
      role="dialog"
      aria-modal="true"
      aria-label={aenderung ? t.chat.menueAbstimmungAendern : t.chat.abstimmungAnlegen}
      data-abstimmungsformular={aenderung ? 'aendern' : 'neu'}
    >
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <Blattkopf titel={t.chat.abstimmung} onSchliessen={onZu} />

        {/* **Kopf, Inhalt und Fuß als Geschwister** (1.61.0), the build from
            the picker "Aus dieser Reise" (1.53.3). This one is the plainest
            case of all: the answer options grow with every tap on "Option
            hinzufügen", and "Abstimmung senden" stood below them. Only this
            box scrolls now.

            The hint after the foot stays after the foot: it says why the
            button is disabled, so it belongs with the button, not with the
            content -- out of sight it would be a silent refusal by detour. */}
        <div className="bogen__rolle">
          <label className="field">
            <span className="field__label">{t.chat.frage}</span>
            <input
              className="field__input"
              type="text"
              maxLength={200}
              placeholder={t.chat.fragePlatzhalter}
              value={frage}
              onChange={(event) => setFrage(event.target.value)}
            />
          </label>

          <span className="field__label">{t.chat.antwortenFeld}</span>
          {antworten.map((wert, i) => (
            <div key={i} className="row row--eng row--knapp">
              <input
                className="field__input field__input--breit"
                type="text"
                maxLength={120}
                placeholder={t.chat.antwortPlatzhalter(i)}
                value={wert}
                onChange={(event) => setzeAntwort(i, event.target.value)}
              />
              {/* Wegnehmen erst ab der dritten: unter zwei wäre es keine
                  Abstimmung mehr, und ein Knopf, der nichts darf, ist einer zu
                  viel. */}
              {!aenderung && antworten.length > MIN_ANTWORTEN && (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Antwort ${i + 1} entfernen`}
                  onClick={() => setAntworten((alt) => alt.filter((_, j) => j !== i))}
                >
                  <XIcon size={18} />
                </button>
              )}
            </div>
          ))}

          {!aenderung && antworten.length < MAX_ANTWORTEN && (
            <button
              type="button"
              className="button button--small button--outline"
              onClick={() => setAntworten((alt) => [...alt, ''])}
            >
              {t.chat.antwortDazu}
            </button>
          )}

          {!aenderung && (
            <label className="row row--eng row--luft">
              <input
                type="checkbox"
                checked={multi}
                onChange={(event) => setMulti(event.target.checked)}
              />
              <span>{t.chat.mehrfachErlauben}</span>
            </label>
          )}

          {/* Up to 1.53.1 a sentence stood here that said what the readers
              will see (percentages, no names). Taken out from use:
              "Erklärungstext in der Abstimmung wegnehmen." The promise itself
              is kept where it is kept -- `Abstimmung` on the server -- and
              stands in the handbook. */}
          {problem && (
            <p className="feld__hinweis" role="alert">
              {problem}
            </p>
          )}
        </div>

        <div className="bogen__fuss">
          <button
            type="button"
            className="button"
            disabled={!bereit || aktion.isPending}
            onClick={() => void sende()}
          >
            {aktion.isPending ? t.allgemein.laeuft : aenderung ? t.allgemein.speichern : t.chat.abstimmungSenden}
          </button>
          <button type="button" className="button button--outline" onClick={onZu}>
            {t.allgemein.abbrechen}
          </button>
        </div>

        {!bereit && (
          <p className="feld__hinweis">{t.chat.abstimmungUnfertig}</p>
        )}
      </div>
    </div>,
    document.body,
  )
}
